import { getCurrentLayout, getCurrentRoomType } from "../../appLayout";
import { ALL_SHOWER_AREA_GROUPS, LOOK_ROOM_TYPES } from "../../look/core/constants";
import { Shower } from "../../look/core/parts/shower";
import { getFeatureCropCoors } from "../../../misc/metaDataFunctions";

// Property name the per-layout commonCoords cache is stored under, directly on the
// featureGroupInterface instance whose bounding box was computed (see zoomFeatureGroup below).
// Namespaced/underscored so it can't collide with any real featureGroupInterface field.
const COMMON_COORDS_CACHE_KEY = "__commonCoordsCacheByLayoutType";

// Same fixed canvas size getCropCoordsCenter (lookFunctions.js) normalizes CropCoords against.
// Deliberately NOT the live canvas element: CropCoords are authored against this reference size,
// and the result has to stay canvas-size independent so it can be memoized.
const CROP_COORDS_CLIENT_WIDTH = 1920;
const CROP_COORDS_CLIENT_HEIGHT = 1080;

// Set rather than ALL_SHOWER_AREA_GROUPS.includes: this runs on every feature click.
const SHOWER_AREA_GROUP_IDS = new Set(ALL_SHOWER_AREA_GROUPS);

// The only room layout the shower-size nudge below applies to.
const SHOWER_AREA_SHIFT_LAYOUT_TYPE = 19;

// Hardcoded horizontal nudge for the shower-area groups on layout 19, keyed by the active
// ShowerSize. Values are a fraction of the canvas width, subtracted from the bounding box's x so
// the framing moves LEFT — the smaller the shower, the further left its features sit relative to
// the box the unfiltered feature lists produce.
//
// This is a nudge on top of the computed box, not a replacement for it: a shower size isn't a
// layout change, so it has no separate cache entry (see applyShowerAreaSizeShift).
const SHOWER_AREA_SHIFT_BY_SIZE: { [showerSize: number]: number } = {
  36: 0.06,
  48: 0.03,
};

type Bounds = {
  min_x: number | undefined;
  min_y: number | undefined;
  max_x: number | undefined;
  max_y: number | undefined;
};

const createBounds = (seedCoords: any): Bounds => ({
  min_x: seedCoords ? seedCoords.min_x : undefined,
  min_y: seedCoords ? seedCoords.min_y : undefined,
  max_x: seedCoords ? seedCoords.max_x : undefined,
  max_y: seedCoords ? seedCoords.max_y : undefined,
});

/**
 * True when the passed feature group is one of the shower-area groups AND we're in the bvLook
 * visualizer - i.e. when the shower-size rules below apply at all.
 *
 * Everywhere else (kitchen, the scene visualizers, the old bathroom) there is no Shower part to ask
 * for a size, so the group is framed by its plain bounding box.
 */
const isShowerAreaGroup = (zoomFeatureGroup: any): boolean =>
  (document.kvConfig.visualizerType === "bvLook") && SHOWER_AREA_GROUP_IDS.has(zoomFeatureGroup.partId);

/**
 * Applies the SHOWER_AREA_SHIFT_BY_SIZE nudge, returning a COPY (never the cached object, and never
 * the caller's own coords) when there is something to shift.
 *
 * Deliberately applied outside the cache, on the way out — on a cache hit as well as on a fresh
 * computation. The cached box is what the feature lists produce and depends only on the room
 * layout; the shower size can change without the layout changing, so baking the nudge into the
 * cached entry would pin whichever size happened to be active when it was first filled.
 *
 * Both edges move by the same amount on purpose. The only thing read off these coords downstream is
 * getCoordsCenter's midpoint (see useZoomToActiveFeatureGroup - the zoom scale comes from
 * featureGroupZoomDefaults, not from the box size), so moving min_x alone would shift the framing
 * by only HALF the value while also widening the box. Shifting both is what makes 0.05 mean an
 * actual 5% of the canvas.
 */
const applyShowerAreaSizeShift = (commonCoords: any, layout: any, zoomFeatureGroup: any): any => {
  if (!layout || (layout.type !== SHOWER_AREA_SHIFT_LAYOUT_TYPE) || !isShowerAreaGroup(zoomFeatureGroup)) {
    return commonCoords;
  }

  const showerPart = layout.parts && layout.parts[Shower.id];
  // Returns the ShowerSize meta value as a string ("36" / "48" / ...), or null when nothing
  // size-bearing is selected.
  const showerSize = (showerPart && showerPart.showerSize) ? showerPart.showerSize() : null;
  const shift = showerSize ? SHOWER_AREA_SHIFT_BY_SIZE[+showerSize] : 0;
  if (!shift) {
    return commonCoords;
  }

  const shiftedCoords = { ...commonCoords };
  if (typeof shiftedCoords.min_x === "number") {
    shiftedCoords.min_x -= shift;
  }
  if (typeof shiftedCoords.max_x === "number") {
    shiftedCoords.max_x -= shift;
  }
  return shiftedCoords;
};

/**
 * Folds one feature list into `bounds` in place.
 *
 * This is getCropCoordsCenter + compareCoords (both in lookFunctions.js) inlined into a single
 * scalar pass, and it is intentionally bit-for-bit equivalent to them, including compareCoords'
 * falsy — not undefined — checks: a 0 already in the accumulator counts as "unset" and gets
 * overwritten by the next feature's value. That quirk is load-bearing for the numbers the zoom
 * offsets in featureGroupZoomDefaults.ts are tuned against, so don't "fix" it here.
 *
 * The inlining is the whole point performance-wise. Feature groups run to tens of thousands of
 * features (see the ~30k note in partCommon.js), and the previous shape allocated, per feature, a
 * coords object from getCropCoordsCenter plus the two array literals + four closure calls
 * compareCoords' forEach pairs create — on top of one throwaway ~30k-element array from
 * [...features, ...featuresParts]. None of that survives the call, so all of it is pure GC
 * pressure on a path that runs on every feature click.
 *
 * @param features list to fold in (featureGroup.features / .featuresParts) - may be undefined
 * @param bounds accumulator, mutated in place
 * @param skipFeaturesWithoutS3Bindings when true, a feature that has a dbFeature but no
 *   s3Bindings on it is excluded from the bounding box (it isn't rendered on the canvas, so its
 *   coords shouldn't drag the framing around)
 * @returns how many features actually contributed coords
 */
const accumulateFeatureBounds = (
  features: any[] | undefined,
  bounds: Bounds,
  skipFeaturesWithoutS3Bindings: boolean,
): number => {
  if (!features || !features.length) {
    return 0;
  }

  let usedCount = 0;

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];
    if (!feature) {
      continue;
    }

    // Not rendered in the visualizer -> not part of what we're framing.
    // NOTE: a dbFeature whose s3Bindings were filtered down to an EMPTY array at load time is
    // still kept by this check. Switch to the codebase's canonical flag - feature
    // .notAvailableInVisualizer, i.e. `!s3Bindings || !s3Bindings.length` - if those should go too.
    const dbFeature = feature.dbFeature;
    if (skipFeaturesWithoutS3Bindings && dbFeature && !dbFeature.s3Bindings) {
      continue;
    }

    const metaData = feature.metaData;
    if (!metaData) {
      continue;
    }

    let minX: number | undefined;
    let minY: number | undefined;
    let maxX: number | undefined;
    let maxY: number | undefined;

    if (metaData["CropCoords"]) {
      // getFeatureCropCoors memoizes its parse on metaData.ParsedCropCoords, so this is a plain
      // property read after the first visit to a given feature.
      const parsedCoords = getFeatureCropCoors(feature);
      if (!parsedCoords) {
        continue; // malformed CropCoords (unparseable string) - previously threw here
      }
      minX = parsedCoords[0] / CROP_COORDS_CLIENT_WIDTH;
      minY = parsedCoords[1] / CROP_COORDS_CLIENT_HEIGHT;
      maxX = parsedCoords[2] / CROP_COORDS_CLIENT_WIDTH;
      maxY = parsedCoords[3] / CROP_COORDS_CLIENT_HEIGHT;
    } else {
      const coords = metaData["Coords"];
      if (!coords) {
        continue; // compareCoords no-ops on a falsy currentCoords
      }
      minX = coords.min_x;
      minY = coords.min_y;
      maxX = coords.max_x;
      maxY = coords.max_y;
    }

    usedCount++;

    if (!bounds.min_x || (bounds.min_x > (minX as number))) {
      bounds.min_x = minX;
    }
    if (!bounds.min_y || (bounds.min_y > (minY as number))) {
      bounds.min_y = minY;
    }
    if (!bounds.max_x || (bounds.max_x < (maxX as number))) {
      bounds.max_x = maxX;
    }
    if (!bounds.max_y || (bounds.max_y < (maxY as number))) {
      bounds.max_y = maxY;
    }
  }

  return usedCount;
};

/**
 * Builds the plain commonCoords object callers expect, omitting axes nothing ever set so that
 * useZoomToActiveFeatureGroup's `!Object.keys(coords).length` bail-out still fires on an empty
 * result.
 */
const toCommonCoords = (bounds: Bounds): any => {
  const commonCoords: any = {};
  if (bounds.min_x !== undefined) {
    commonCoords.min_x = bounds.min_x;
  }
  if (bounds.min_y !== undefined) {
    commonCoords.min_y = bounds.min_y;
  }
  if (bounds.max_x !== undefined) {
    commonCoords.max_x = bounds.max_x;
  }
  if (bounds.max_y !== undefined) {
    commonCoords.max_y = bounds.max_y;
  }
  return commonCoords;
};

/**
 * Same bounding-box computation as getCommonCoords (see
 * src/bvcomponents/look/lookNavigation/lookFunctions.js), but memoized per featureGroup, per
 * layout type — e.g. `{ 18: {min_x, min_y, max_x, max_y}, 19: {...} }` (same numeric layout-type
 * keys as byLayoutType in featureGroupZoomDefaults.js).
 *
 * getCommonCoords' layout.designNavigationItems({part: zoomFeatureGroup}, ...) walk is the
 * expensive part of this computation, and its result only ever changes when the room layout
 * changes or the feature group's own features change — it doesn't need to be recomputed on every
 * zoom-to-feature-group call (e.g. every feature click within the same still-active group). This
 * wrapper caches that result directly on the target featureGroup instance, so it lives exactly as
 * long as the featureGroup itself and needs no separate invalidation bookkeeping.
 *
 * The cache is only ever written once `allFeaturesReceived` is true on the featureGroup whose
 * bounding box is being computed (`zoomFeatureGroup` below, not necessarily the `featureGroup`
 * passed in — see the edge_profile/center_island_edge redirect). Before all features are in, the
 * navItems walk would produce an incomplete/still-growing bounding box, and memoizing that would
 * freeze a wrong answer in place for the rest of the session.
 *
 * Features with a dbFeature but no s3Bindings on it are excluded from the box — they have no layer
 * on the canvas, so framing to them would zoom to something that isn't visible.
 *
 * The shower-area groups on layout 19 get a hardcoded horizontal nudge per ShowerSize applied on
 * the way out, outside the cache — see applyShowerAreaSizeShift.
 *
 * @param featureGroup {featureGroupInterface}
 * @param clickedFeature {featureInterface|null}
 * @returns {object} commonCoords - {min_x, min_y, max_x, max_y}
 */
export const getFeatureGroupCommonCoords = (
  featureGroup: any,
  clickedFeature: any = null,
): any => {
  if (!featureGroup) {
    return {};
  }

  const layout = getCurrentLayout();
  let zoomFeatureGroup;
  switch (featureGroup.partId) {
    case "edge_profile":
      zoomFeatureGroup = layout.getFeatureGroupByPartId("countertop") || featureGroup;
      break;
    case "center_island_edge":
      zoomFeatureGroup = layout.getFeatureGroupByPartId("center_island_countertop") || featureGroup;
      break;
    default:
      zoomFeatureGroup = featureGroup;
      break;
  }

  const layoutType = layout && layout.type;
  const cacheByLayoutType = zoomFeatureGroup[COMMON_COORDS_CACHE_KEY];
  const cachedCoords = cacheByLayoutType && cacheByLayoutType[layoutType];
  if (cachedCoords) {
    return applyShowerAreaSizeShift(cachedCoords, layout, zoomFeatureGroup);
  }

  // Seed, not mutate: metaData.Coords is shared with dbFeature.metaData.Coords, and the previous
  // version handed that very object to compareCoords, which widens it in place - permanently
  // replacing the clicked feature's own coords with the whole group's bounding box.
  const seedCoords = clickedFeature ? clickedFeature.metaData && clickedFeature.metaData.Coords : null;

  // .features / .featuresParts are getters (they resolve the current layout name on every access),
  // so read each exactly once and walk the two arrays in place instead of spreading them into a
  // third throwaway array.
  const groupFeatures: any[] = zoomFeatureGroup.features;
  const groupFeaturesParts: any[] = zoomFeatureGroup.featuresParts;

  let bounds = createBounds(seedCoords);
  const usedCount =
    accumulateFeatureBounds(groupFeatures, bounds, true) +
    accumulateFeatureBounds(groupFeaturesParts, bounds, true);

  if (!usedCount && !seedCoords) {
    // Every feature in the group was filtered out and there's no clicked feature to fall back on,
    // so we'd return {} and the caller would silently skip the zoom entirely. Recompute unfiltered
    // rather than lose the framing - degraded, but still better than no zoom at all.
    bounds = createBounds(null);
    accumulateFeatureBounds(groupFeatures, bounds, false);
    accumulateFeatureBounds(groupFeaturesParts, bounds, false);
  }

  const commonCoords = toCommonCoords(bounds);

  if (
    getCurrentRoomType() === LOOK_ROOM_TYPES.as &&
    ((zoomFeatureGroup.partId === "front_wall") || (zoomFeatureGroup.partId === "right_wall"))
  ) {
    // because of the reflection in mirror, it sets min_x to 0.1, so the menu is not scrolling on open
    commonCoords.min_x = 0.8;
  }

  if (zoomFeatureGroup.allFeaturesReceived) {
    zoomFeatureGroup[COMMON_COORDS_CACHE_KEY] = {
      ...(cacheByLayoutType || {}),
      [layoutType]: commonCoords,
    };
  }

  return applyShowerAreaSizeShift(commonCoords, layout, zoomFeatureGroup);
};

export default getFeatureGroupCommonCoords;
